import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { useAuth } from '../../services/AuthContext';

interface SkillDistribution {
  [key: string]: {
    avg_score: number;
    count: number;
    trend: string;
  };
}

interface CodeQualityMetrics {
  avg_lint_score: number;
  avg_complexity: number;
  avg_modularity: number;
  avg_test_coverage: number;
  common_issues: string[];
}

export const AnalyticsDashboard: React.FC = () => {
  const { user } = useAuth();
  const [skillData, setSkillData] = useState<SkillDistribution>({});
  const [codeQualityData, setCodeQualityData] = useState<CodeQualityMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user?.organizationId) {
        setError('No organization found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const [skillsRes, codeQualityRes] = await Promise.all([
          fetch(`/api/v1/analytics/org/${user.organizationId}/skills`),
          fetch(`/api/v1/analytics/org/${user.organizationId}/code-quality`)
        ]);

        if (!skillsRes.ok || !codeQualityRes.ok) {
          throw new Error('Failed to fetch analytics data');
        }

        const skillsData = await skillsRes.json();
        const codeQualityData = await codeQualityRes.json();

        setSkillData(skillsData.skill_distribution);
        setCodeQualityData(codeQualityData);
      } catch (error) {
        console.error('Error fetching analytics:', error);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user?.organizationId) {
      fetchAnalytics();
    } else if (user) {
      setError('No organization found');
      setLoading(false);
    }
  }, [user?.organizationId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading analytics</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const skillChartData = Object.entries(skillData).map(([skill, data]) => ({
    name: skill,
    score: data.avg_score,
    count: data.count
  }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
      
      {/* Skills Overview */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Skills Distribution</h2>
        </div>
        <div className="px-6 py-4">
          <div className="h-80">
            <LineChart width={800} height={300} data={skillChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="score" stroke="#8884d8" />
              <Line type="monotone" dataKey="count" stroke="#82ca9d" />
            </LineChart>
          </div>
        </div>
      </div>

      {/* Code Quality Metrics */}
      {codeQualityData && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Code Quality Metrics</h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-gray-700">Lint Score</h3>
                <p className="text-2xl font-bold text-gray-900">{Math.round(codeQualityData.avg_lint_score)}%</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700">Code Complexity</h3>
                <p className="text-2xl font-bold text-gray-900">{Math.round(codeQualityData.avg_complexity)}</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700">Modularity</h3>
                <p className="text-2xl font-bold text-gray-900">{Math.round(codeQualityData.avg_modularity)}%</p>
              </div>
              <div>
                <h3 className="font-medium text-gray-700">Test Coverage</h3>
                <p className="text-2xl font-bold text-gray-900">{Math.round(codeQualityData.avg_test_coverage)}%</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 